'use client';

import { useState, useCallback, useRef, createContext, useContext } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Link as LinkIcon } from 'lucide-react';
import { trackCopy } from '@/lib/analytics';
import { isCloudflareImageUrl, cfImage, cfImageSrcSet, SIZES } from '@/lib/cloudflare-images';
import CloudProviderCalculator from '@/components/blog/calculators/CloudProviderCalculator';
import CollapsibleEmbed from '@/components/blog/CollapsibleEmbed';
import InlineLeadMagnet from '@/components/InlineLeadMagnet';
import { LEAD_MAGNET_CONFIG } from '@/lib/data/lead-magnets';
import DynamicToolLoader from '@/components/DynamicToolLoader';
import ToolPreviewCard from '@/components/ToolPreviewCard';
import { getToolBySlug } from '@/lib/data/tools';
import dynamic from 'next/dynamic';
import { getVendorDataset } from '@/lib/data/vendor-datasets';

const VendorPicker = dynamic(() => import('@/components/blog/VendorPicker'));
import VendorPickerCard from '@/components/blog/VendorPickerCard';

/**
 * Vendor pickers render every vendor as a full card (blurb + highlights +
 * tradeoffs + pricing), so a large dataset becomes ~3,000px tall and swamps the
 * article. Above this vendor count we embed a compact <VendorPickerCard> that
 * links to the canonical /tools/comparison/{toolSlug} page instead of inlining
 * the full picker. Small pickers (≤ this many) still render inline.
 */
const MAX_INLINE_PICKER_VENDORS = 5;

/** Context to signal that a <code> element is rendered inside a <pre> block */
const PreContext = createContext(false);

interface MarkdownContentProps {
  content: string;
  className?: string;
  /** Page slug for lead magnet tracking */
  pageSlug?: string;
  /** Category for lead magnet tracking */
  category?: string;
}

/**
 * Copy button component for code blocks
 * Enhanced for better visibility and discoverability
 */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      trackCopy('code_block');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        trackCopy('code_block');
        setTimeout(() => setCopied(false), 2000);
      } catch {
        console.error('Failed to copy');
      }
      document.body.removeChild(textArea);
    }
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      className="copy-btn-hit-area absolute top-0 right-0 z-10 p-2"
      aria-label={copied ? 'Copied!' : 'Copy to clipboard'}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-gray-800/90 text-white hover:bg-gray-700 hover:scale-105 transition-all shadow-lg backdrop-blur-sm border border-gray-600/50">
        {copied ? (
          <>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Copied!
          </>
        ) : (
          <>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Copy
          </>
        )}
      </span>
    </button>
  );
}

/**
 * Copyable code block - clicking anywhere on the block copies the code
 * Distinguishes clicks from text selection: only copies if user didn't select text
 */
function CopyableCodeBlock({ children, text }: { children: React.ReactNode; text: string }) {
  const [showOverlay, setShowOverlay] = useState(false);
  const mouseDownPos = useRef<{ x: number; y: number } | null>(null);

  const copyText = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      trackCopy('code_block');
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        trackCopy('code_block');
      } catch { /* ignore */ }
      document.body.removeChild(textArea);
    }
    setShowOverlay(true);
    setTimeout(() => setShowOverlay(false), 1500);
  }, [text]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    mouseDownPos.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    // Don't copy if click was on the copy button
    if ((e.target as HTMLElement).closest('.copy-btn-hit-area')) return;

    // Don't copy if user dragged (text selection)
    if (mouseDownPos.current) {
      const dx = Math.abs(e.clientX - mouseDownPos.current.x);
      const dy = Math.abs(e.clientY - mouseDownPos.current.y);
      if (dx > 5 || dy > 5) return;
    }

    // Don't copy if text is selected
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) return;

    copyText();
  }, [copyText]);

  return (
    <div
      className="relative group code-block-wrapper"
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    >
      {children}
      <CopyButton text={text} />
      {showOverlay && (
        <div className="code-block-copied-overlay" aria-live="polite">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Copied!
        </div>
      )}
    </div>
  );
}

/**
 * Inline code with click-to-copy functionality
 * Shows visual feedback when clicked
 * Uses CSS classes that properly handle code in tables with !important rules
 *
 * Skips interactivity (role/tabIndex) when the content contains an email address.
 * Cloudflare's email obfuscation rewrites emails into <a href="/cdn-cgi/l/email-protection..."> tags
 * post-render. A focusable <a> inside a focusable <code role="button"> triggers
 * axe-core nested-interactive violations.
 */
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;

function CopyableInlineCode({ children }: { children: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  const text = getTextContent(children);
  const containsEmail = EMAIL_PATTERN.test(text);

  const handleClick = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      trackCopy('inline_code');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        trackCopy('inline_code');
        setTimeout(() => setCopied(false), 1500);
      } catch {
        console.error('Failed to copy');
      }
      document.body.removeChild(textArea);
    }
  }, [text]);

  if (containsEmail) {
    return <code className="inline-code-interactive" suppressHydrationWarning>{children}</code>;
  }

  if (hasInteractiveChild(children)) {
    return <code>{children}</code>;
  }

  return (
    <code
      onClick={handleClick}
      className={`inline-code-interactive ${copied ? 'code-copied' : ''}`}
      title={copied ? 'Copied!' : 'Click to copy'}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {copied ? (
        <span className="inline-flex items-center gap-1">
          <svg className="w-3 h-3 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {children}
        </span>
      ) : (
        children
      )}
    </code>
  );
}

/**
 * Extract text content from React children
 */
function getTextContent(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (!children) return '';
  if (Array.isArray(children)) return children.map(getTextContent).join('');
  if (typeof children === 'object' && 'props' in children) {
    const element = children as React.ReactElement<{ children?: React.ReactNode }>;
    return getTextContent(element.props?.children);
  }
  return '';
}

function hasInteractiveChild(children: React.ReactNode): boolean {
  if (!children) return false;
  if (Array.isArray(children)) return children.some(hasInteractiveChild);
  if (typeof children === 'object' && 'props' in children) {
    const element = children as React.ReactElement<{ children?: React.ReactNode; href?: string; onClick?: unknown }>;
    if (element.props.href || element.props.onClick) return true;
    return hasInteractiveChild(element.props.children);
  }
  return false;
}

/**
 * Convert heading text to a URL-friendly slug
 */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Parse heading text for custom ID syntax: "Heading Text {#custom-id}"
 * Returns the clean text and the ID (custom or auto-generated)
 */
function parseHeadingId(text: string): { cleanText: string; id: string } {
  // Match {#custom-id} at the end of the heading
  const match = text.match(/^(.+?)\s*\{#([a-z0-9-]+)\}\s*$/i);
  if (match) {
    return {
      cleanText: match[1].trim(),
      id: match[2].toLowerCase(),
    };
  }
  return {
    cleanText: text,
    id: slugify(text),
  };
}

/**
 * Heading component with anchor link
 * Provides GitHub-style anchor links for deep linking to sections
 * Supports custom ID syntax: "Heading Text {#custom-id}"
 */
function HeadingWithAnchor({
  level,
  children,
}: {
  level: 2 | 3 | 4;
  children: React.ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  const rawText = getTextContent(children);
  const { cleanText, id } = parseHeadingId(rawText);
  const HeadingTag = `h${level}` as 'h2' | 'h3' | 'h4';

  // If we have a custom ID, we need to render the clean text instead of children
  const hasCustomId = rawText !== cleanText;

  const handleCopyLink = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    const url = `${window.location.origin}${window.location.pathname}#${id}`;

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        console.error('Failed to copy link');
      }
      document.body.removeChild(textArea);
    }
  }, [id]);

  return (
    <HeadingTag id={id} className="heading-with-anchor group">
      <a
        href={`#${id}`}
        onClick={handleCopyLink}
        className="heading-anchor-link"
        aria-label={`Link to ${cleanText}`}
        title={copied ? 'Link copied!' : 'Click to copy link'}
      >
        <LinkIcon
          className={`heading-anchor-icon ${copied ? 'copied' : ''}`}
          size={level === 2 ? 20 : level === 3 ? 18 : 16}
        />
      </a>
      <span className="heading-text">
        {hasCustomId ? cleanText : children}
        {copied && <span className="heading-copied-badge">Link copied!</span>}
      </span>
    </HeadingTag>
  );
}

/**
 * Strip YAML frontmatter from markdown content
 * Frontmatter is delimited by --- at the start and end
 */
function stripFrontmatter(content: string): string {
  // Match frontmatter: starts with ---, ends with ---, at the beginning of content
  const frontmatterRegex = /^---\s*\n[\s\S]*?\n---\s*\n?/;
  return content.replace(frontmatterRegex, '').trim();
}

/**
 * Client component for rendering Markdown content
 * Used to display educational content from database
 * Supports GitHub Flavored Markdown (tables, strikethrough, etc.)
 * Includes copy-to-clipboard buttons for code blocks
 * Inline code is click-to-copy for better UX
 * Headings include anchor links for deep linking
 */
export default function MarkdownContent({ content, className = 'prose prose-lg max-w-none', pageSlug, category }: MarkdownContentProps) {
  // Strip any YAML frontmatter that might be in the content
  const cleanContent = stripFrontmatter(content);

  // Cloudflare's Email Address Obfuscation rewrites @-patterns into
  // <a class="__cf_email__">[email protected]</a> in the SSR response,
  // which diverges from React's client tree and trips React #418 hydration
  // mismatches on articles that mention emails inside code blocks
  // (gr_ypr5396 — windows-update-commands). suppressHydrationWarning on
  // the prose wrapper accepts the edge-rewritten DOM as authoritative.
  return (
    <div className={className} suppressHydrationWarning>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        // Allow inline image data-URIs (e.g. self-contained animated SVG diagrams in
        // first-party blog content). react-markdown's default urlTransform strips
        // data: URIs; we permit only data:image/ and defer everything else to the
        // default sanitizer. SVGs loaded via <img> cannot execute scripts.
        urlTransform={(url) => (url.startsWith('data:image/') ? url : defaultUrlTransform(url))}
        components={{
          // Convert h1 to h2 to avoid multiple h1 tags per page (SEO best practice)
          // Pages already have their own h1 in the hero section
          h1: ({ children }) => (
            <HeadingWithAnchor level={2}>{children}</HeadingWithAnchor>
          ),
          h2: ({ children }) => (
            <HeadingWithAnchor level={2}>{children}</HeadingWithAnchor>
          ),
          h3: ({ children }) => (
            <HeadingWithAnchor level={3}>{children}</HeadingWithAnchor>
          ),
          h4: ({ children }) => (
            <HeadingWithAnchor level={4}>{children}</HeadingWithAnchor>
          ),
          // Wrap tables in a scrollable container so they don't overflow into the sidebar.
          // tabIndex + role + aria-label keep the region keyboard-accessible (axe scrollable-region-focusable).
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          table: ({ children, node, ...props }) => (
            <div
              className="overflow-x-auto -mx-1 px-1"
              tabIndex={0}
              role="region"
              aria-label="Table"
            >
              <table {...props}>{children}</table>
            </div>
          ),
          // Ensure all images have alt text for SEO and accessibility
          // Use responsive Cloudflare Images for better performance.
          // Strip any author-supplied width/height/style so wide WordPress-era
          // images can't blow past the column on mobile (390px); the className
          // pins max-width:100% + height:auto so the image always fits its
          // container and keeps its aspect ratio. This is defense-in-depth
          // alongside the global prose rules.
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          img: ({ src, alt, title, node, width, height, style, ...props }) => {
            const srcStr = typeof src === 'string' ? src.trim() : '';
            if (!srcStr) return null;
            const altText = alt || title || `Image: ${srcStr.split('/').pop()?.split('?')[0] || 'content image'}`;
            const responsiveClass = 'h-auto max-w-full';

            // For Cloudflare Images, use responsive srcSet
            if (isCloudflareImageUrl(srcStr)) {
              const srcSet = cfImageSrcSet(srcStr, 'content');
              const defaultSrc = cfImage(srcStr, { width: 1024, quality: 85 });

              // eslint-disable-next-line @next/next/no-img-element
              return (
                <img
                  src={defaultSrc}
                  srcSet={srcSet}
                  sizes={SIZES.content}
                  alt={altText}
                  title={title}
                  loading="lazy"
                  className={responsiveClass}
                  {...props}
                />
              );
            }

            // eslint-disable-next-line @next/next/no-img-element
            return <img src={srcStr} alt={altText} title={title} loading="lazy" className={responsiveClass} {...props} />;
          },
          // Raw <iframe> embeds (YouTube etc.) ship a fixed width="560" in the
          // markup. Wrap them in a responsive 16:9 container and force the
          // iframe to fill it so they never overflow the column on mobile.
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          iframe: ({ node, width, height, style, className, ...props }) => (
            <span className="not-prose block relative my-6 w-full max-w-full overflow-hidden rounded-lg" style={{ aspectRatio: '16 / 9' }}>
              <iframe className="absolute inset-0 h-full w-full" {...props} />
            </span>
          ),
          // Raw inline <video> / <svg> can carry intrinsic or attribute widths
          // wider than the mobile column. Constrain them to the container.
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          video: ({ node, width, height, style, className, children, ...props }) => (
            <video className="h-auto max-w-full" {...props}>{children}</video>
          ),
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          svg: ({ node, width, height, style, className, children, ...props }) => (
            <svg className="h-auto max-w-full" {...props}>{children}</svg>
          ),
          // tabIndex + role + aria-label keep the scrollable region keyboard-accessible
          // (axe scrollable-region-focusable). Prose CSS gives <pre> overflow-x: auto.
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          pre: ({ children, node, ...props }) => {
            const codeText = getTextContent(children);
            // suppressHydrationWarning: Cloudflare's Email Address Obfuscation
            // rewrites @-patterns inside code into <a class="__cf_email__"> tags
            // post-SSR, diverging from React's tree and tripping React #418
            // (gr_ypr5396). Accept the edge-rewritten DOM as authoritative.
            return (
              <PreContext.Provider value={true}>
                <CopyableCodeBlock text={codeText}>
                  <pre {...props} tabIndex={0} role="region" aria-label="Code block" suppressHydrationWarning>{children}</pre>
                </CopyableCodeBlock>
              </PreContext.Provider>
            );
          },
          // Inline code gets click-to-copy functionality
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          code: ({ children, className, node, ...props }) => {
            // Check if this code element is inside a <pre> block via context
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const insidePre = useContext(PreContext);

            // Code inside <pre> is always block code — render without interactive class
            if (insidePre) {
              return <code className={className} {...props} suppressHydrationWarning>{children}</code>;
            }

            // Inline code - CSS will handle table context via .prose table code selectors
            return <CopyableInlineCode {...props}>{children}</CopyableInlineCode>;
          },
          // GFM task list items render as `<input type="checkbox" disabled />` with no
          // accessible name, triggering axe `label` violations. Add aria-label since the
          // visible label is the sibling text in the <li>, which axe can't associate.
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          input: ({ node, ...props }) => {
            if (props.type === 'checkbox') {
              return <input {...props} aria-label="Task list item" />;
            }
            return <input {...props} />;
          },
          // Detect <div data-calculator="type"> in markdown and lazy-load calculator
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          div: ({ children, node, ...props }) => {
            const calcType = (props as Record<string, unknown>)['data-calculator'] ?? (props as Record<string, unknown>)['dataCalculator'];
            if (typeof calcType === 'string' && calcType) {
              // Calculators have no canonical page to link out to, so guard
              // against a tall embed swamping the article by clamping its
              // height with an expandable wrapper instead.
              return (
                <CollapsibleEmbed maxPx={720} label="Show full calculator">
                  <CloudProviderCalculator type={calcType} />
                </CollapsibleEmbed>
              );
            }
            const magnetType = (props as Record<string, unknown>)['data-lead-magnet'] ?? (props as Record<string, unknown>)['dataLeadMagnet'];
            if (typeof magnetType === 'string' && LEAD_MAGNET_CONFIG[magnetType]) {
              const cfg = LEAD_MAGNET_CONFIG[magnetType];
              return (
                <InlineLeadMagnet
                  headline={cfg.headline}
                  subtext={cfg.subtext}
                  asset={cfg.asset}
                  pageSlug={pageSlug || ''}
                  category={category}
                  magnetKey={magnetType}
                />
              );
            }
            // Tutorial posts use data-tool-cta after an embedded video when the
            // tool is the reader's next action, not part of the lesson itself.
            // Always render a compact card here, even when the tool supports a
            // live article embed.
            const toolCta = (props as Record<string, unknown>)['data-tool-cta'] ?? (props as Record<string, unknown>)['dataToolCta'];
            if (typeof toolCta === 'string' && toolCta) {
              const bundleSlug = toolCta.split('/').filter(Boolean).pop() || toolCta;
              return <ToolPreviewCard slug={bundleSlug} />;
            }
            const toolSlug = (props as Record<string, unknown>)['data-tool'] ?? (props as Record<string, unknown>)['dataTool'];
            if (typeof toolSlug === 'string' && toolSlug) {
              // Accept either "category/slug" (matches tools.ts link) or bare "slug" (matches TOOL_MAPPINGS).
              // DynamicToolLoader fetches the R2 bundle by bare slug.
              const bundleSlug = toolSlug.split('/').filter(Boolean).pop() || toolSlug;
              const tool = getToolBySlug(bundleSlug);
              // Tall/stateful tools (embed: 'card' in the registry — wizards,
              // planners, assessors, simulators, multi-step builders) are too
              // tall to inline; render a compact preview card linking to the
              // canonical tool page instead of the full <DynamicToolLoader>.
              // 'hero' tools reaching this point are embedded mid-article (the
              // leading-embed case is extracted by blog/[slug]/page.tsx and
              // rendered full-width above the article/TOC grid), so they get
              // the same compact-card treatment to avoid a squeezed render.
              if (tool?.embed === 'card' || tool?.embed === 'hero') {
                return <ToolPreviewCard slug={bundleSlug} />;
              }
              const seoContent = tool ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-800">
                  <h3 className="mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">
                    {tool.name}
                  </h3>
                  <p className="mb-3 text-sm text-gray-700 dark:text-gray-300">
                    {tool.description}
                  </p>
                  <a
                    href={tool.link}
                    className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                  >
                    Open the full {tool.name} tool →
                  </a>
                </div>
              ) : undefined;
              return <DynamicToolLoader slug={bundleSlug} seoContent={seoContent} embedded />;
            }
            const pickerKey = (props as Record<string, unknown>)['data-vendor-picker'] ?? (props as Record<string, unknown>)['dataVendorPicker'];
            if (typeof pickerKey === 'string' && pickerKey) {
              const dataset = getVendorDataset(pickerKey);
              if (dataset) {
                // Tall pickers swamp the article — link out instead of inlining.
                if (dataset.vendors.length > MAX_INLINE_PICKER_VENDORS) {
                  return <VendorPickerCard dataset={dataset} />;
                }
                return <VendorPicker dataset={dataset} />;
              }
            }
            return <div {...props}>{children}</div>;
          },
        }}
      >
        {cleanContent}
      </ReactMarkdown>
    </div>
  );
}
